const EMPTY_LAP_TIME = "--:--.---";

/**
 * Format lap time from milliseconds to MM:SS.mmm format
 */
export function formatLapTime(timeMs: number): string {
  if (!timeMs || timeMs <= 0) return EMPTY_LAP_TIME;

  const totalSeconds = timeMs / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const milliseconds = Math.floor(timeMs % 1000);

  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}.${milliseconds.toString().padStart(3, "0")}`;
}

/**
 * Format speed to one decimal place with unit
 */
export function formatSpeed(speedKmh: number | undefined): string {
  if (speedKmh === undefined || speedKmh === null) return "--";
  return `${speedKmh.toFixed(1)} km/h`;
}

/**
 * Calculate time difference and format with +/- prefix
 */
export function formatTimeDiff(timeMs: number, referenceMs: number): string {
  if (!timeMs || !referenceMs) return "--";

  const diffMs = timeMs - referenceMs;
  const isNegative = diffMs < 0;
  const absDiff = Math.abs(diffMs);

  if (absDiff < 10) return "±0.00";

  const seconds = absDiff / 1000;
  const prefix = isNegative ? "-" : "+";

  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${prefix}${minutes}:${remainingSeconds.toFixed(3).padStart(6, "0")}`;
  }

  return `${prefix}${seconds.toFixed(3)}`;
}

/**
 * Parse lap time string (MM:SS.mmm) to milliseconds
 */
export function parseLapTime(timeString: string): number {
  if (!timeString || timeString === EMPTY_LAP_TIME) return 0;

  const parts = timeString.split(":");
  if (parts.length !== 2) return 0;

  const minutes = parseInt(parts[0]);
  const secondsParts = parts[1].split(".");

  if (secondsParts.length !== 2) return 0;

  const seconds = parseInt(secondsParts[0]);
  const milliseconds = parseInt(secondsParts[1]);

  return (minutes * 60 + seconds) * 1000 + milliseconds;
}

const windowDateFmt = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});
const windowTimeFmt = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
});

/**
 * Format a capture's time window for compact display. Same-day windows
 * compress to one date and a shared day-period ("Oct 3 · 08:56 – 08:57 AM");
 * cross-day windows keep both dates ("Oct 3, 08:57 PM → Oct 4, 05:15 AM").
 */
export function formatWindow(
  firstStartedAt: string | null,
  lastEndedAt: string | null,
  locale: string | undefined = undefined,
): string {
  if (!firstStartedAt && !lastEndedAt) return "—";
  const dateFmt =
    locale === undefined
      ? windowDateFmt
      : new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" });
  const timeFmt =
    locale === undefined
      ? windowTimeFmt
      : new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" });

  const start = firstStartedAt ? new Date(firstStartedAt) : null;
  const end = lastEndedAt ? new Date(lastEndedAt) : null;
  if (!start || !end) {
    const only = (start ?? end)!;
    return `${dateFmt.format(only)}, ${timeFmt.format(only)}`;
  }

  if (dateFmt.format(start) !== dateFmt.format(end)) {
    return `${dateFmt.format(start)}, ${timeFmt.format(start)} → ${dateFmt.format(end)}, ${timeFmt.format(end)}`;
  }

  const clockOf = (parts: Intl.DateTimeFormatPart[]) =>
    parts
      .filter(
        (p) =>
          p.type === "hour" || p.type === "minute" || p.type === "literal",
      )
      .map((p) => p.value)
      .join("")
      .trim();
  const periodOf = (parts: Intl.DateTimeFormatPart[]) =>
    (parts.find((p) => p.type === "dayPeriod")?.value ?? "").trim();

  const from = timeFmt.formatToParts(start);
  const to = timeFmt.formatToParts(end);
  const fromClock = clockOf(from);
  const toClock = clockOf(to);
  const fromPeriod = periodOf(from);
  const toPeriod = periodOf(to);
  if (fromPeriod === toPeriod) {
    return `${dateFmt.format(start)} · ${fromClock} – ${toClock}${toPeriod ? ` ${toPeriod}` : ""}`;
  }
  return `${dateFmt.format(start)} · ${timeFmt.format(start)} – ${timeFmt.format(end)}`;
}
